import { motion } from 'framer-motion';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import { tokens } from '../theme/tokens';

const MaskOverlay = styled(Box)<{ fadeColor?: string }>`
	height: 60px;
	margin-top: -60px;
	z-index: 1;
	background: white;
	mask: linear-gradient(transparent, ${(props) => props.fadeColor ?? tokens.surface.primary});
`;

const AnimationContainer = styled(motion.div)`
	overflow: hidden;
`;

const ReadMore = styled.a`
	cursor: pointer;
	color: ${tokens.text.secondary};
	text-decoration: underline;

	&:hover {
		color: ${(props) => props.theme.colors.primary};

		&:active {
			color: ${(props) => props.theme.colors.primary};
		}
	}
`;

type Props = {
	textAlignment?: 'start' | 'center' | 'end';
	fadeColor?: string;
	children?: ReactNode;
};

export const Snippet: FC<Props> = ({ textAlignment = 'center', fadeColor, children }) => {
	const ref = useRef<HTMLDivElement>(null);
	const [height, setHeight] = useState(0);
	const [isExpanded, setIsExpanded] = useState(false);

	useEffect(() => {
		if (ref.current) {
			const rect = ref.current.getBoundingClientRect!();
			setHeight(rect.height);
		}
	}, [children]);

	return (
		<Box>
			<AnimationContainer
				initial={false}
				animate={{ height: isExpanded ? 'auto' : '90px' }}
				transition={{ duration: 0.4, ease: 'easeInOut' }}
			>
				<Box ref={ref}>{children}</Box>
			</AnimationContainer>

			{!isExpanded && <MaskOverlay fadeColor={fadeColor} />}

			{height > 60 && (
				<Box textAlign={textAlignment} mt={isExpanded ? 2 : 0}>
					<ReadMore onClick={() => setIsExpanded(!isExpanded)}>
						{isExpanded ? 'Read Less' : 'Read More'}
					</ReadMore>
				</Box>
			)}
		</Box>
	);
};
